import { useCallback, useRef, useState } from "react";

const SpeechRecognitionAPI =
  typeof window !== "undefined"
    ? //@ts-ignore
      window?.SpeechRecognition || window?.webkitSpeechRecognition
    : null;

export const useSpeechRecognition = ({ lang = "en-US" } = {}) => {
  const recognitionRef = useRef(null);
  const finalTranscriptRef = useRef("");
  const [interimText, setInterimText] = useState("");
  const [isSupported] = useState(!!SpeechRecognitionAPI);

  const start = useCallback(() => {
    if (!SpeechRecognitionAPI) return;

    finalTranscriptRef.current = "";
    setInterimText("");

    const recognition = new SpeechRecognitionAPI();
    recognition.lang = lang;
    recognition.continuous = true;
    recognition.interimResults = true;

    recognition.onresult = (event) => {
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcriptPiece = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscriptRef.current += `${transcriptPiece} `;
        } else {
          interim += transcriptPiece;
        }
      }
      setInterimText(interim);
    };

    recognition.onerror = (event) => {
      console.error("Speech recognition error:", event.error);
    };

    // auto-restart if the browser cuts the session short while still recording
    recognition.onend = () => {
      if (recognitionRef.current === recognition) {
        try {
          recognition.start();
        } catch {
          // already stopped/cleaned up, ignore
        }
      }
    };

    recognitionRef.current = recognition;
    recognition.start();
  }, [lang]);

  const stop = useCallback(() => {
    const recognition = recognitionRef.current;
    recognitionRef.current = null; // prevents onend auto-restart
    if (recognition) recognition.stop();
    setInterimText("");
  }, []);

  const getTranscript = useCallback(
    () => (finalTranscriptRef.current + interimText).trim(),
    [interimText],
  );

  return { start, stop, interimText, getTranscript, isSupported };
};
